// @ts-expect-error - SCSS module imported for styling
import './CopAgreementDetails.scss'

import { useEffect, useMemo, useState } from 'react'

import { COP_COMMON_ERROR_MESSAGES } from '../../constants/common-error-messages'
import { ToastService } from '../../services/toast.service'
import { fetchAgreementDetails } from './services/CopAgreementDetails.service'

export type CopAgreementDetailsProps = {
  readonly title?: string
  readonly sectiontype?: string
  readonly layout?: string
  readonly labels?: string
}

type HeaderLabel = {
  labelText: string
  dataKey: string
}

function parseLabels(labels?: string): HeaderLabel[] {
  if (!labels) return []

  try {
    const parsed = JSON.parse(labels)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item): HeaderLabel | null => {
        if (typeof item === 'string') {
          return { labelText: item, dataKey: '' }
        }
        if (item && typeof item === 'object') {
          const labelText = typeof item.labelText === 'string' ? item.labelText.trim() : ''
          const dataKey = typeof item.dataKey === 'string' ? item.dataKey.trim() : ''
          if (labelText) {
            return { labelText, dataKey }
          }
        }
        return null
      })
      .filter((entry): entry is HeaderLabel => entry !== null)
  } catch {
    return []
  }
}

export default function CopAgreementDetails({
  title,
  sectiontype,
  layout,
  labels,
}: CopAgreementDetailsProps) {
  const [apiData, setApiData] = useState<Record<string, string>>({})

  const isAgreementSection = sectiontype === 'agreement' || sectiontype === 'agreements'
  const parsedLabels = useMemo(
    () => (isAgreementSection ? parseLabels(labels) : []),
    [isAgreementSection, labels],
  )

  // Fetch API data on mount
  useEffect(() => {
    let cancelled = false

    fetchAgreementDetails()
      .then(response => {
        if (cancelled || !response) return

        // Format dates with better error handling
        const formatDate = (dateStr?: string) => {
          if (!dateStr) {
            return ''
          }

          const date = new Date(dateStr)
          if (Number.isNaN(date.getTime())) {
            return ''
          }
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        }

        const formattedData = {
          status: response.status || '',
          agreementNumber: response.number || '',
          validityFrom: formatDate(response.validFrom),
          validityTo: formatDate(response.validTo),
          legalEntityName: response.legalEntityName || '',
        }

        setApiData(formattedData)
      })
      .catch(err => {
        const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
          '{{action}}',
          COP_COMMON_ERROR_MESSAGES['api-error'].action['agreement-details-loading'],
        )
        const message = err instanceof Error && err.message ? err.message : fallbackMessage
        ToastService.error(message)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Update the AEM title element with the real agreement number from the API.
  // Also removes any stale "Agreement ID: ..." container created by agreement-details.ts.
  useEffect(() => {
    if (!apiData.agreementNumber) return
    const titleEl = document.getElementById('cop-agreement-details-title')
    if (!titleEl) return
    // Remove the container injected by agreement-details.ts (shows wrong URL-based ID)
    titleEl.parentElement?.querySelector('.cop-agreement-id-container')?.remove()
    // Preserve the AEM-authored title text as the prefix (store once to prevent double-append on re-render)
    if (!titleEl.dataset.originalText) {
      titleEl.dataset.originalText = titleEl.textContent?.trim() ?? ''
    }
    let prefix = titleEl.dataset.originalText
    // Remove trailing colon if present to avoid double colon
    if (prefix?.endsWith(':')) {
      prefix = prefix.slice(0, -1).trim()
    }
    titleEl.textContent = prefix
      ? `${prefix}: ${apiData.agreementNumber}`
      : apiData.agreementNumber
  }, [apiData.agreementNumber])

  // Helper to check if label matches validity pattern
  const isValidityLabel = (normalized: string): boolean => {
    return normalized.includes('validity') || normalized.includes('validty')
  }

  // Helper to get status indicator
  const getStatusIndicator = (status: string): 'active' | 'inactive' | undefined => {
    const statusLower = status.toLowerCase()
    if (statusLower === 'active') return 'active'
    if (statusLower === 'inactive') return 'inactive'
    return undefined
  }

  type ValueResult = { value: string; statusIndicator?: 'active' | 'inactive' }

  const getValueByDataKey = (dataKey: string): ValueResult | null => {
    switch (dataKey) {
      case 'status': {
        const status = apiData.status || ''
        return { value: status, statusIndicator: getStatusIndicator(status) }
      }
      case 'number':
        return { value: apiData.agreementNumber || '—' }
      case 'validFrom':
        return { value: apiData.validityFrom || '—' }
      case 'validTo':
        return { value: apiData.validityTo || '—' }
      case 'legalEntityName':
        return { value: apiData.legalEntityName || '—' }
      default:
        return null
    }
  }

  const getValueByLabel = (labelText: string): ValueResult => {
    const normalized = labelText.toLowerCase().trim()

    if (normalized.includes('status')) {
      const status = apiData.status || ''
      return { value: status, statusIndicator: getStatusIndicator(status) }
    }

    if (normalized.includes('agreement') || normalized.includes('number') || normalized.includes('agreemnet')) {
      return { value: apiData.agreementNumber || '—' }
    }

    if (isValidityLabel(normalized) && normalized.includes('from')) {
      return { value: apiData.validityFrom || '—' }
    }

    if (isValidityLabel(normalized) && normalized.includes('to')) {
      return { value: apiData.validityTo || '—' }
    }

    if (normalized.includes('legal') || normalized.includes('entity')) {
      return { value: apiData.legalEntityName || '—' }
    }

    return { value: '—' }
  }

  const getValue = (label: HeaderLabel): ValueResult => {
    if (label.dataKey) {
      const byKey = getValueByDataKey(label.dataKey)
      if (byKey) return byKey
    }
    return getValueByLabel(label.labelText)
  }

  // Don't render if no labels
  if (parsedLabels.length === 0) return null

  // Check if there's an AEM title element to avoid duplication
  const hasAemTitle = document.getElementById('cop-agreement-details-title')
  // Only show title if explicitly provided and there's no AEM title
  const shouldShowTitle = !hasAemTitle && (title || apiData.agreementNumber)
  const headerTitle = title || (apiData.agreementNumber ? `Agreement Details: ${apiData.agreementNumber}` : '')
  const layoutClass = layout ? ` cop-agreement-details__summary--${layout}` : ''

  return (
    <div className="cop-agreement-details">
      {shouldShowTitle && headerTitle && (
        <h1 className="cop-agreement-details__title">{headerTitle}</h1>
      )}

      <div className={`cop-agreement-details__summary${layoutClass}`}>
        {parsedLabels.map((label, index) => {
          const { value, statusIndicator } = getValue(label)
          const isWide =
            label.dataKey === 'legalEntityName' ||
            (!label.dataKey &&
              (label.labelText.toLowerCase().includes('legal') || label.labelText.toLowerCase().includes('entity')))

          return (
            <div
              key={`${label.dataKey || label.labelText}-${index}`}
              className={`cop-agreement-details__summary-cell${isWide ? ' cop-agreement-details__summary-cell--wide' : ''}`}
            >
              <div className="cop-agreement-details__summary-label">{label.labelText}</div>
              <div className="cop-agreement-details__summary-value">
                {statusIndicator === 'active' && (
                  <span className="cop-agreement-details__dot cop-agreement-details__dot--green" />
                )}
                {statusIndicator === 'inactive' && (
                  <span className="cop-agreement-details__dot cop-agreement-details__dot--red" />
                )}
                {value}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}